import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Eye, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import BooleanPill from '../../component/common/BooleanPill';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import { RECURRENCE_UNIT_OPTIONS } from './Form';

// Detalhe do serviço — drawer lateral somente-leitura (mesma largura/estilo
// do drawer de Lançamentos, `pages/transactions/List.js`) preenchido com o
// `detail-grid` de `prototipo-lista-servicos.html` (label uppercase + valor,
// Descrição e Custo externo ocupando a linha inteira).
function DetailItem({ label, value, muted = false, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

export default function ServiceViewDialog({ open, onClose, serviceId, onRequestEdit, canEdit = true }) {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [service, setService] = useState({});
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    const usesVehicle = Boolean(enterpriseSettings?.os_use_vehicle);
    const unitLabel = (unit) => RECURRENCE_UNIT_OPTIONS.find((o) => o.id === unit)?.name || '';

    const fetchService = async (id) => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/services/${id}`);
            setService(response.data);
        } catch (error) {
            console.error('Erro ao carregar dados do serviço:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (serviceId && open) {
            fetchService(serviceId);
        }
    }, [serviceId, open]);

    const handleClose = () => {
        setService({});
        setLoading(true);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                    borderRadius: 0,
                    boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                    borderLeft: '1px solid', borderColor: 'divider',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden'
                }
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'info.bg',
                            color: 'primary.main',
                            mb: '10px',
                        }}>
                            <Eye size={18} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                            {loading ? 'Carregando…' : (service.name || '—')}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            Detalhes do serviço cadastrado.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" aria-label="Fechar">
                        <X size={15} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px 18px' }}>
                            <DetailItem label="Categoria" value={service.category_name || '—'} />
                            <DetailItem label="Subcategoria" value={service.subcategory_name || '—'} />
                            <DetailItem full label="Descrição" value={service.description || 'Nenhuma descrição fornecida'} muted={!service.description} />
                            <DetailItem label="Valor" value={`R$ ${service.price ?? '0,00'}`} />
                            <DetailItem label="Serviço externo" value={<BooleanPill value={!!service.is_external} />} />
                            <DetailItem
                                full
                                label="Custo externo"
                                value={service.is_external ? `R$ ${service.external_price ?? '0,00'}` : 'Não se aplica — serviço não é externo.'}
                                muted={!service.is_external}
                            />
                            {usesVehicle && (
                                <>
                                    <Box sx={{ gridColumn: '1 / -1', borderTop: '1px solid', borderColor: 'divider', pt: '14px' }}>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 800, color: 'text.primary' }}>
                                            Recorrência
                                        </Typography>
                                    </Box>
                                    <DetailItem
                                        full={!service.is_recurring}
                                        label="Recorrente"
                                        value={service.is_recurring ? 'Sim' : 'Não'}
                                    />
                                    {service.is_recurring && (
                                        <>
                                            <DetailItem
                                                label="Intervalo"
                                                value={service.recurrence_interval
                                                    ? `${service.recurrence_interval} ${unitLabel(service.recurrence_interval_unit)}`
                                                    : '—'}
                                            />
                                            <DetailItem
                                                label="Lembrete"
                                                value={service.recurrence_reminder_days
                                                    ? `${service.recurrence_reminder_days} dias antes`
                                                    : '—'}
                                            />
                                            <DetailItem
                                                label="KM"
                                                value={service.recurrence_km
                                                    ? `A cada ${Number(service.recurrence_km).toLocaleString('pt-BR')} km`
                                                    : '—'}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.25, justifyContent: 'flex-end', flexShrink: 0 }}>
                    <Button variant="outlined" onClick={handleClose}>Fechar</Button>
                    {canEdit && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                const svc = service;
                                handleClose();
                                if (onRequestEdit) onRequestEdit(svc);
                            }}
                        >
                            Editar
                        </Button>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}
